/**
 * Embeddable-mode helpers (ADR-002).
 *
 * When the page is loaded with ``?embed=1`` it is being iframed by the parent app: chrome (header,
 * history sidebar) is hidden via a body class and the document reports its content height to the
 * parent over postMessage so the parent can size the iframe.
 *
 * Security (ADR-002): framing itself is locked by the host's ``Content-Security-Policy:
 * frame-ancestors`` header (see index.html comment) — the JS cannot enforce that. What the JS DOES
 * enforce is the message contract: we only POST our height, never a token, and never with
 * ``targetOrigin: '*'``. The allowed parent origin comes from ``VITE_EMBED_PARENT_ORIGIN``; until
 * the real parent spec lands it defaults to the wildcard ONLY for the height ping (which carries no
 * secret) so the generic shell works in any harness — see the note on _parentOrigin below.
 */

export function isEmbedded(): boolean {
  return new URLSearchParams(window.location.search).get('embed') === '1';
}

// The parent origin to address height messages to. A height ping carries no secret, so a missing
// config falls back to '*' to keep the generic embeddable shell usable before the parent spec is
// known (ADR-002). Any FUTURE token exchange must NOT reuse this fallback — it must require a
// configured origin and validate event.origin on receipt.
const _parentOrigin: string =
  (import.meta.env.VITE_EMBED_PARENT_ORIGIN as string | undefined) ?? '*';

interface HeightMessage {
  type: 'kroupy:height';
  height: number;
}

/**
 * Start reporting document height to the parent frame on load, resize, and DOM mutations.
 * No-op when not embedded. Returns a disposer that stops the observers.
 */
export function startHeightReporting(): () => void {
  if (!isEmbedded() || window.parent === window) return () => {};

  const post = (): void => {
    const height = Math.ceil(document.documentElement.scrollHeight);
    const message: HeightMessage = { type: 'kroupy:height', height };
    window.parent.postMessage(message, _parentOrigin);
  };

  const resizeObserver = new ResizeObserver(post);
  resizeObserver.observe(document.documentElement);
  window.addEventListener('load', post);
  post();

  return () => {
    resizeObserver.disconnect();
    window.removeEventListener('load', post);
  };
}
